import { useEffect, useState } from "react";
import { X, Download, Share2 } from "lucide-react";

const DISMISS_KEY = "ccfi-pwa-dismissed-at";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Shows a compact install banner on the bottom of mobile screens.
 * - Chrome/Android/Edge: intercepts `beforeinstallprompt` and shows an "Install" button.
 * - iOS Safari: no install event exists, so we display step-by-step instructions.
 * - Hidden when running as an installed PWA (display-mode: standalone).
 * - User can dismiss for 7 days.
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed → never show
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return;

    // Dismissed recently → skip
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    if (iOS) {
      // Show iOS instructions after a small delay so we don't blast users on first paint
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="lg:hidden fixed left-3 right-3 z-40 bg-slate-900 text-white shadow-xl rounded-sm border border-cyan-500/30"
      style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="w-9 h-9 shrink-0 bg-cyan-600 flex items-center justify-center rounded-sm">
          <Download size={18} strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="stamp-label text-cyan-400 mb-0.5">Install CCFI - Compass</div>
          {isIOS ? (
            <p className="text-xs text-slate-200 leading-snug">
              Tap <Share2 size={12} className="inline mx-0.5 -mt-0.5" /> Share → <span className="font-semibold">Add to Home Screen</span> to install this app.
            </p>
          ) : (
            <p className="text-xs text-slate-200 leading-snug">
              Install to your home screen for one-tap access — works offline for the shell.
            </p>
          )}
          {!isIOS && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="mt-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold rounded-sm tracking-wide uppercase"
            >
              Install
            </button>
          )}
        </div>
        <button
          data-testid="pwa-dismiss-btn"
          onClick={dismiss}
          className="text-slate-400 hover:text-white p-1 shrink-0"
          aria-label="Dismiss install prompt"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
